import { capitalize, orderBy } from 'lodash';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ICaisoResource } from 'src/domain/caiso-resource/interface';
import { useScResourcesListQuery } from 'src/domain/caiso-resource/queries';
import { CloudVenDispatchTypeTableLabelMap } from '../../../../components/selectors/CloudVenDispatchByMethodSelect/CloudVenDispatchTypeMethodSelect';
import { filterCompaniesByType } from '../../../../components/selectors/GlobalCompanySelector/companyHelpers';
import { useAlertTrackingUsersListQuery } from '../../../../domain/alert-track/queries';
import { COMPANY_TYPE, ICompany } from '../../../../domain/company/interface';
import { useCompanyListQuery } from '../../../../domain/company/queries';
import { ITrackingUser, AlertTrackParticipationStatusType } from '../../../../domain/event/interface';
import { IFilterOptionValue, IFilterOption } from '../../../../domain/filter-options/interface';
import {
    useAccountManagersListQuery,
    useMeterEnrollmentEnrollmentIdsListQuery,
    usePjmLocationZipCodesListQuery,
    usePjmRegistrationSeasonsListQuery,
    useSiteOptionsListQuery,
    useVendorBillItemProgramsListQuery,
    useVendorBillItemUtilitiesListQuery,
    useVendorBillTransactionIdsListQuery,
} from '../../../../domain/filter-options/queries';
import { supportedEnergyMarkets } from '../../../../domain/market-prices/interface';
import { useMarketZoneListQuery } from '../../../../domain/market/queries';
import { ENROLLMENT_STATUS_TEXT_MAP } from '../../../../domain/meter-enrollment/interface';
import { NOMINATION_RECENCY_TEXT_MAP } from '../../../../domain/nomination/interface';
import { IProgram } from '../../../../domain/program/interface';
import { ProgramUtilityList } from '../../../../domain/program/programUtility';
import { useProgramListQuery } from '../../../../domain/program/queries';
import { ISelectOption } from '../../../../domain/saved-filters/interface';
import { SanInfoSource } from '../../../../domain/site/interface';
import { useSiteUtilitiesListQuery } from '../../../../domain/site/queries';
import { IUser } from '../../../../domain/user/interface';
import { useUserListQuery } from '../../../../domain/user/queries';
import { VenStatus } from '../../../../domain/ven/interface';
import { DISPATCH_TYPE_TEXT_MAP, SITE_INTERVAL_DATA_FILTER_VALUES, SITE_STATUS_TEXT_MAP } from '../../../sites/siteInterface';
import { usePageLocation } from '../../../usePageState';
import { PjmLocationStatus, PjmRegistrationStatus, PjmResourceType, PjmProductType, PjmRegistrationLeadTime } from '../../../../domain/pjm/dto/enums';
import { EDC_VALUES, STATE, ZONE } from '../../../../domain/pjm/values';
import { SyncStatus } from '../../../../domain/pjm/dto/SyncStatus';

export interface ISidebarFilterOptionsContext {
    isLoading: boolean;
    allCompaniesOptions: ISelectOption<number | string>[];
    controlProviderOptions: ISelectOption<number | string>[];
    utilityCustomerOptions: ISelectOption<number | string>[];
    customerCompaniesOptions: ISelectOption<number | string>[];
    programOptions: ISelectOption<number | string>[];
    utilityOptions: ISelectOption<number | string>[];
    accountManagerOptions: ISelectOption<string>[];
    isoOptions: ISelectOption<string>[];
    programUtilityOptions: ISelectOption<string>[];
    marketOptions: ISelectOption<string>[];
    siteOptions: ISelectOption<number | string>[];
    siteStatusOptions: ISelectOption<string>[];
    dispatchTypeOptions: ISelectOption<string>[];
    meterEnrollmentEnrollmentIdOptions: ISelectOption<string>[];
    meterEnrollmentOptions: ISelectOption<string>[];
    meterEnrollmentStatusOptions: ISelectOption<string>[];
    meterOptions: ISelectOption<string>[];
    nominationRecencyOptions: ISelectOption<string>[];
    hasVenOptions: ISelectOption<string>[];
    nominationPresetOptions: ISelectOption<string>[];
    siteIntervalDataOptions: ISelectOption<string>[];
    closedSitesOptions: ISelectOption<string>[];
    dateRangePresetOptions: ISelectOption<string>[];
    eventDurationPresetOptions: ISelectOption<string>[];
    agreementStatusOptions: ISelectOption<string>[];
    intervalDataSourceOptions: ISelectOption<string>[];
    venTypeOptions: ISelectOption<string>[];
    venStatusOptions: ISelectOption<string>[];
    resourceTelemetryStatusOptions: ISelectOption<string>[];
    marketAwardSourceOptions: ISelectOption<string>[];
    marketAwardStatusOptions: ISelectOption<string>[];
    caisoResourceOptions: ISelectOption<number | string>[];
    vendorBillItemPeriodOptions: ISelectOption<string>[];
    vendorBillItemUtilityOptions: ISelectOption<string>[];
    vendorBillItemProgramOptions: ISelectOption<string>[];
    vendorBillTransactionIdOptions: ISelectOption<string>[];
    hasEventOptions: ISelectOption<string>[];
    alertTrackingProgramOptions: ISelectOption<string>[];
    alertTrackingPeriodOptions: ISelectOption<string>[];
    alertTrackingUserOptions: ISelectOption<string>[];
    eventCompanyParticipationProgramOptions: ISelectOption<string>[];
    eventStatusOptions: ISelectOption<string>[];
    alertTrackStatusOptions: ISelectOption<string>[];
    alertTrackOpenedOptions: ISelectOption<string>[];
    alertTrackParticipationStatusOptions: ISelectOption<string>[];
    cloudVenDispatchTypeOptions: ISelectOption<string>[];
    userOptions: ISelectOption<number>[];
    nominationReadinessTrackingStatusOptions: ISelectOption<string>[];
    nominationStatusOptions: ISelectOption<string>[];
    eventCompanyParticipationPeriodOptions: ISelectOption<string>[];
    pjmLocationStatusOptions: ISelectOption<string>[];
    pjmLocationZoneOptions: ISelectOption<string>[];
    pjmLocationStateOptions: ISelectOption<string>[];
    pjmLocationZipCodeOptions: ISelectOption<string>[];
    pjmLocationEdcOptions: ISelectOption<string>[];
    pjmLocationSyncDrhubStatusOptions: ISelectOption<string>[];
    pjmRegistrationStatusOptions: ISelectOption<string>[];
    pjmRegistrationZoneOptions: ISelectOption<string>[];
    pjmRegistrationEdcOptions: ISelectOption<string>[];
    pjmRegistrationResourceTypeOptions: ISelectOption<string>[];
    pjmRegistrationProductOptions: ISelectOption<string>[];
    pjmRegistrationLeadTimeOptions: ISelectOption<string>[];
    pjmRegistrationCompletedOptions: ISelectOption<string>[];
    pjmRegistrationUnderDuplicateReviewOptions: ISelectOption<string>[];
    pjmRegistrationSyncDrhubStatusOptions: ISelectOption<string>[];
    pjmRegistrationSeasonOptions: ISelectOption<string>[];
}

export const SidebarFilterOptionsContext = React.createContext<ISidebarFilterOptionsContext>({} as ISidebarFilterOptionsContext);

enum FiltersEnum {
    COMPANIES = 'companies',
    SITES = 'sites',
    SITE_UTILITIES = 'site_utilities',
    PROGRAMS = 'programs',
    MARKET_ZONES = 'market_zones',
    SC_RESOURCES = 'sc_resources',
    VENDOR_BILL_ITEM_PROGRAMS = 'vendor_bill_item_programs',
    VENDOR_BILL_ITEM_UTILITIES = 'vendor_bill_item_utilities',
    VENDOR_BILL_TRANSACTION_IDS = 'vendor_bill_transaction_ids',
    ALERT_TRACKING_USERS = 'alert_tracking_users',
    USERS = 'users',
    ALERT_TRACKING_PROGRAMS = 'alert_tracking_programs',
    READINESS_TRACKING = 'readiness_tracking',
    ACCOUNT_MANAGER = 'account_manager',
    PJM_LOCATION_ZIP_CODES = 'pjm_location_zip_codes',
    PJM_REGISTRATION_SEASONS = 'pjm_registration_seasons',
    METER_ENROLLMENT_ENROLLMENT_ID = 'meter_enrollment_enrollment_id',
    EVENT_COMPANY_PARTICIPATION = 'event_company_participation',
}

const pageFilters: { [key: string]: FiltersEnum[] } = {
    '/sites': [FiltersEnum.COMPANIES, FiltersEnum.SITE_UTILITIES, FiltersEnum.MARKET_ZONES, FiltersEnum.PROGRAMS],
    '/nominations': [
        FiltersEnum.COMPANIES,
        FiltersEnum.SITES,
        FiltersEnum.MARKET_ZONES,
        FiltersEnum.PROGRAMS,
        FiltersEnum.ACCOUNT_MANAGER,
        FiltersEnum.METER_ENROLLMENT_ENROLLMENT_ID,
    ],
    '/ven-devices/cloud-ven': [FiltersEnum.PROGRAMS],
    '/ven-devices/customer-ven': [FiltersEnum.COMPANIES],
    '/energy-usage-data/analytics': [
        FiltersEnum.COMPANIES,
        FiltersEnum.SITE_UTILITIES,
        FiltersEnum.MARKET_ZONES,
        FiltersEnum.PROGRAMS,
        FiltersEnum.SC_RESOURCES,
    ],
    '/settlement-data': [
        FiltersEnum.MARKET_ZONES,
        FiltersEnum.VENDOR_BILL_ITEM_PROGRAMS,
        FiltersEnum.VENDOR_BILL_ITEM_UTILITIES,
        FiltersEnum.VENDOR_BILL_TRANSACTION_IDS,
    ],
    '/events/tracking': [FiltersEnum.ALERT_TRACKING_PROGRAMS, FiltersEnum.ALERT_TRACKING_USERS],
    '/events/company-participation': [FiltersEnum.EVENT_COMPANY_PARTICIPATION],
    '/event-analytics/performance': [FiltersEnum.PROGRAMS],
    '/caiso/cmri/overview': [FiltersEnum.SC_RESOURCES],
    '/admin/agreements': [FiltersEnum.COMPANIES],
    '/nomination-readiness-tracking': [FiltersEnum.PROGRAMS, FiltersEnum.USERS],
    '/pjm/locations': [FiltersEnum.PJM_LOCATION_ZIP_CODES],
    '/pjm/registrations': [FiltersEnum.PJM_REGISTRATION_SEASONS],
};

const isFilterEnabled = (pathname: string, filter: FiltersEnum): boolean => {
    return !!pageFilters[pathname]?.includes(filter);
};

export const SidebarFilterOptionsProvider = ({ children }: { children: React.ReactNode }) => {
    const location = useLocation();
    const { queryToState } = usePageLocation();
    const { search, pathname } = location;

    const { companyId } = queryToState(search);

    const siteUtilityListQuery = useSiteUtilitiesListQuery({
        enabled: isFilterEnabled(pathname, FiltersEnum.SITE_UTILITIES),
    });
    const companyListQuery = useCompanyListQuery({}, { enabled: isFilterEnabled(pathname, FiltersEnum.COMPANIES) });
    const siteListQuery = useSiteOptionsListQuery(companyId ? { company_id: companyId } : {}, {
        enabled: isFilterEnabled(pathname, FiltersEnum.SITES),
    });
    const meterEnrollmentEnrollmentIdListQuery = useMeterEnrollmentEnrollmentIdsListQuery(companyId ? { company_id: companyId } : {}, {
        enabled: isFilterEnabled(pathname, FiltersEnum.METER_ENROLLMENT_ENROLLMENT_ID),
    });

    const programListQuery = useProgramListQuery(companyId ? { company_id: companyId } : {}, {
        enabled: isFilterEnabled(pathname, FiltersEnum.PROGRAMS),
    });
    const alertTrackingProgramListQuery = useProgramListQuery({}, { enabled: isFilterEnabled(pathname, FiltersEnum.ALERT_TRACKING_PROGRAMS) });
    const eventCompanyParticipationProgramListQuery = useProgramListQuery(
        {},
        { enabled: isFilterEnabled(pathname, FiltersEnum.EVENT_COMPANY_PARTICIPATION) }
    );
    const marketZoneListQuery = useMarketZoneListQuery({ enabled: isFilterEnabled(pathname, FiltersEnum.MARKET_ZONES) });
    const scResourcesListQuery = useScResourcesListQuery(
        { pagination: { pageSize: 1000 } },
        { enabled: isFilterEnabled(pathname, FiltersEnum.SC_RESOURCES) }
    );
    const vendorBillItemProgramListQuery = useVendorBillItemProgramsListQuery(companyId ? { company_id: companyId } : {}, {
        enabled: isFilterEnabled(pathname, FiltersEnum.VENDOR_BILL_ITEM_PROGRAMS),
    });
    const vendorBillItemUtilitiesListQuery = useVendorBillItemUtilitiesListQuery(companyId ? { company_id: companyId } : {}, {
        enabled: isFilterEnabled(pathname, FiltersEnum.VENDOR_BILL_ITEM_UTILITIES),
    });
    const vendorBillTransactionIdsListQuery = useVendorBillTransactionIdsListQuery(companyId ? { company_id: companyId } : {}, {
        enabled: isFilterEnabled(pathname, FiltersEnum.VENDOR_BILL_TRANSACTION_IDS),
    });
    const alertTrackingUserListQuery = useAlertTrackingUsersListQuery(companyId, {
        enabled: isFilterEnabled(pathname, FiltersEnum.ALERT_TRACKING_USERS),
    });
    const userListQuery = useUserListQuery(companyId ? { company_id: companyId } : {}, { enabled: isFilterEnabled(pathname, FiltersEnum.USERS) });
    const accountManagerListQuery = useAccountManagersListQuery(companyId ? { company_id: companyId } : {}, {
        enabled: isFilterEnabled(pathname, FiltersEnum.ACCOUNT_MANAGER),
    });

    const pjmLocationZipCodesListQuery = usePjmLocationZipCodesListQuery({
        enabled: isFilterEnabled(pathname, FiltersEnum.PJM_LOCATION_ZIP_CODES),
    });

    const pjmRegistrationSeasonsListQuery = usePjmRegistrationSeasonsListQuery({
        enabled: isFilterEnabled(pathname, FiltersEnum.PJM_REGISTRATION_SEASONS),
    });

    const { allCompaniesOptions, controlProviderOptions, utilityCustomerOptions, customerCompaniesOptions } = useCompanyOptions(
        companyListQuery.data?.data || []
    );
    const siteOptions = useSiteOptions(siteListQuery.data || []);
    const meterEnrollmentEnrollmentIdOptions = useMeterEnrollmentEnrollmentIdOptions(meterEnrollmentEnrollmentIdListQuery.data || []);
    const programOptions = useProgramOptions(programListQuery.data?.data || []);
    const utilityOptions = useUtilityOptions(siteUtilityListQuery.data || []);
    const marketOptions = useMarketOptions(marketZoneListQuery.data || {});
    const caisoResourceOptions = useCaisoResourcesOptions(scResourcesListQuery.data?.data || []);
    const vendorBillItemProgramOptions = useVendorBillItemProgramOptions(vendorBillItemProgramListQuery.data || []);
    const vendorBillItemUtilityOptions = useVendorBillItemUtilityOptions(vendorBillItemUtilitiesListQuery.data || []);
    const vendorBillTransactionIdOptions = useVendorBillTransactionIdOptions(vendorBillTransactionIdsListQuery.data || []);
    const alertTrackingProgramOptions = useAlertTrackProgramOptions(alertTrackingProgramListQuery.data?.data || []);
    const alertTrackingUserOptions = useAlertTrackUserOptions(alertTrackingUserListQuery?.data || []);
    const eventCompanyParticipationProgramOptions = useEventCompanyParticipationProgramOptions(
        eventCompanyParticipationProgramListQuery.data?.data || []
    );
    const userOptions = useUserOptions(userListQuery.data?.data || []);
    const accountManagerOptions = useAccountMangerOptions(accountManagerListQuery.data || []);
    const pjmLocationZipCodeOptions = usePjmLocationZipCodeOptions(pjmLocationZipCodesListQuery.data || []);
    const pjmRegistrationSeasonOptions = usePjmRegistrationSeasonOptions(pjmRegistrationSeasonsListQuery.data || []);

    const isLoading = useMemo(() => {
        const queries = [
            { enabled: isFilterEnabled(pathname, FiltersEnum.SITE_UTILITIES), ...siteUtilityListQuery },
            { enabled: isFilterEnabled(pathname, FiltersEnum.COMPANIES), ...companyListQuery },
            { enabled: isFilterEnabled(pathname, FiltersEnum.SITES), ...siteListQuery },
            { enabled: isFilterEnabled(pathname, FiltersEnum.METER_ENROLLMENT_ENROLLMENT_ID), ...meterEnrollmentEnrollmentIdListQuery },
            { enabled: isFilterEnabled(pathname, FiltersEnum.PROGRAMS), ...programListQuery },
            { enabled: isFilterEnabled(pathname, FiltersEnum.MARKET_ZONES), ...marketZoneListQuery },
            { enabled: isFilterEnabled(pathname, FiltersEnum.SC_RESOURCES), ...scResourcesListQuery },
            { enabled: isFilterEnabled(pathname, FiltersEnum.VENDOR_BILL_ITEM_PROGRAMS), ...vendorBillItemProgramListQuery },
            { enabled: isFilterEnabled(pathname, FiltersEnum.VENDOR_BILL_ITEM_UTILITIES), ...vendorBillItemUtilitiesListQuery },
            { enabled: isFilterEnabled(pathname, FiltersEnum.VENDOR_BILL_TRANSACTION_IDS), ...vendorBillTransactionIdsListQuery },
            { enabled: isFilterEnabled(pathname, FiltersEnum.ALERT_TRACKING_USERS), ...alertTrackingUserListQuery },
            { enabled: isFilterEnabled(pathname, FiltersEnum.ALERT_TRACKING_PROGRAMS), ...alertTrackingProgramListQuery },
            { enabled: isFilterEnabled(pathname, FiltersEnum.USERS), ...userListQuery },
            { enabled: isFilterEnabled(pathname, FiltersEnum.ACCOUNT_MANAGER), ...accountManagerListQuery },
            { enabled: isFilterEnabled(pathname, FiltersEnum.PJM_LOCATION_ZIP_CODES), ...pjmLocationZipCodesListQuery },
            { enabled: isFilterEnabled(pathname, FiltersEnum.PJM_REGISTRATION_SEASONS), ...pjmRegistrationSeasonsListQuery },
        ];

        return queries.some(q => q.enabled && q.isLoading);
    }, [
        pathname,
        siteUtilityListQuery,
        companyListQuery,
        siteListQuery,
        meterEnrollmentEnrollmentIdListQuery,
        programListQuery,
        marketZoneListQuery,
        scResourcesListQuery,
        vendorBillItemProgramListQuery,
        vendorBillItemUtilitiesListQuery,
        vendorBillTransactionIdsListQuery,
        alertTrackingUserListQuery,
        alertTrackingProgramListQuery,
        userListQuery,
        accountManagerListQuery,
        pjmLocationZipCodesListQuery,
        pjmRegistrationSeasonsListQuery,
    ]);

    const contextValue: ISidebarFilterOptionsContext = useMemo(
        () => ({
            isLoading,
            allCompaniesOptions,
            siteOptions,
            meterEnrollmentEnrollmentIdOptions,
            controlProviderOptions,
            utilityCustomerOptions,
            customerCompaniesOptions,
            isoOptions: ISO_OPTIONS,
            programUtilityOptions: PROGRAM_UTILITY_OPTIONS,
            programOptions,
            utilityOptions,
            marketOptions,
            siteStatusOptions: SITE_STATUS_OPTIONS,
            dispatchTypeOptions: DISPATCH_TYPE_OPTIONS,
            meterEnrollmentOptions: METER_ENROLLMENT_OPTIONS,
            meterEnrollmentStatusOptions: METER_ENROLLMENT_STATUS_OPTIONS,
            meterOptions: METER_OPTIONS,
            nominationRecencyOptions: NOMINATION_RECENCY_OPTIONS,
            hasVenOptions: HAS_VEN_OPTIONS,
            nominationPresetOptions: NOMINATION_PRESET_OPTIONS,
            siteIntervalDataOptions: SITE_INTERVAL_DATA_OPTIONS,
            closedSitesOptions: CLOSED_SITES_OPTIONS,
            dateRangePresetOptions: DATE_RANGE_PRESET_OPTIONS,
            eventDurationPresetOptions: EVENT_DURATION_PRESET_OPTIONS,
            agreementStatusOptions: AGREEMENT_STATUS_OPTIONS,
            intervalDataSourceOptions: INTERVAL_DATA_SOURCE_OPTIONS,
            venTypeOptions: VEN_TYPE_OPTIONS,
            venStatusOptions: VEN_STATUS_OPTIONS,
            resourceTelemetryStatusOptions: RESOURCE_TELEMETRY_STATUS_OPTIONS,
            marketAwardSourceOptions: MARKET_AWARD_SOURCE_OPTIONS,
            marketAwardStatusOptions: MARKET_AWARD_STATUS_OPTIONS,
            caisoResourceOptions,
            vendorBillItemPeriodOptions: VENDOR_BILL_ITEM_PERIOD_OPTIONS,
            vendorBillItemProgramOptions,
            vendorBillItemUtilityOptions,
            vendorBillTransactionIdOptions,
            hasEventOptions: HAS_EVENT_OPTIONS,
            alertTrackingProgramOptions: alertTrackingProgramOptions,
            alertTrackingPeriodOptions: ALERT_TRACKING_PERIOD_OPTIONS,
            alertTrackingUserOptions: alertTrackingUserOptions,
            alertTrackStatusOptions: ALERT_TRACK_STATUS_OPTIONS,
            eventCompanyParticipationProgramOptions: eventCompanyParticipationProgramOptions,
            eventStatusOptions: EVENT_STATUS_OPTIONS,
            alertTrackOpenedOptions: ALERT_TRACK_OPENED_OPTIONS,
            alertTrackParticipationStatusOptions: ALERT_TRACK_PARTICIPATION_OPTION,
            cloudVenDispatchTypeOptions: CLOUD_VEN_DISPATCH_TYPE_OPTIONS,
            userOptions,
            nominationReadinessTrackingStatusOptions: NOMINATION_READINESS_TRACKING_STATUS_OPTIONS,
            nominationStatusOptions: NOMINATION_STATUS_OPTIONS,
            accountManagerOptions,
            eventCompanyParticipationPeriodOptions: EVENT_COMPANY_PARTICIPATION_PERIOD_OPTIONS,
            pjmLocationStatusOptions: PJM_LOCATION_STATUS_FILTER,
            pjmLocationZoneOptions: PJM_ZONE_FILTER,
            pjmLocationStateOptions: PJM_LOCATION_STATE_FILTER,
            pjmLocationZipCodeOptions,
            pjmLocationEdcOptions: PJM_EDC_FILTER,
            pjmLocationSyncDrhubStatusOptions: PJM_SYNC_DRHUB_STATUS_FILTER,
            pjmRegistrationStatusOptions: PJM_REGISTRATION_STATUS_FILTER,
            pjmRegistrationZoneOptions: PJM_ZONE_FILTER,
            pjmRegistrationEdcOptions: PJM_EDC_FILTER,
            pjmRegistrationResourceTypeOptions: PJM_REGISTRATION_RESOURCE_TYPE_FILTER,
            pjmRegistrationProductOptions: PJM_REGISTRATION_PRODUCT_FILTER,
            pjmRegistrationLeadTimeOptions: PJM_REGISTRATION_LEAD_TIME_FILTER,
            pjmRegistrationCompletedOptions: PJM_REGISTRATION_YES_NO_FILTER,
            pjmRegistrationUnderDuplicateReviewOptions: PJM_REGISTRATION_YES_NO_FILTER,
            pjmRegistrationSyncDrhubStatusOptions: PJM_SYNC_DRHUB_STATUS_FILTER,
            pjmRegistrationSeasonOptions,
        }),
        [
            isLoading,
            allCompaniesOptions,
            siteOptions,
            meterEnrollmentEnrollmentIdOptions,
            controlProviderOptions,
            utilityCustomerOptions,
            customerCompaniesOptions,
            programOptions,
            utilityOptions,
            marketOptions,
            caisoResourceOptions,
            vendorBillItemProgramOptions,
            vendorBillItemUtilityOptions,
            vendorBillTransactionIdOptions,
            alertTrackingProgramOptions,
            alertTrackingUserOptions,
            eventCompanyParticipationProgramOptions,
            userOptions,
            accountManagerOptions,
            pjmLocationZipCodeOptions,
        ]
    );

    return <SidebarFilterOptionsContext.Provider value={contextValue}>{children}</SidebarFilterOptionsContext.Provider>;
};

export const useSidebarFilterOptionsContext = () => {
    const context = React.useContext(SidebarFilterOptionsContext);

    if (!context) {
        throw new Error('useSidebarFilterOptionsContext must be used within a SidebarFilterOptionsProvider');
    }

    return context;
};

// Static option definitions
const SITE_STATUS_OPTIONS = orderBy(
    Object.entries(SITE_STATUS_TEXT_MAP).map(([key, value]) => ({
        label: value,
        value: key,
    })),
    'label'
);

const DISPATCH_TYPE_OPTIONS = orderBy(
    Object.entries(DISPATCH_TYPE_TEXT_MAP).map(([value, label]) => ({
        label,
        value,
    })),
    'label'
);

const METER_ENROLLMENT_OPTIONS = [
    { label: 'Found at Salesforce', value: 'true' },
    { label: 'Not found at Salesforce', value: 'false' },
];

const METER_OPTIONS = [
    { label: 'Linked Meters', value: 'true' },
    { label: 'Not Linked Meters', value: 'false' },
];

// Static option definitions
const ISO_OPTIONS = orderBy(
    supportedEnergyMarkets.map(market => ({
        label: market.toUpperCase(),
        value: market,
        key: market,
    })),
    'label'
);

// Static option definitions
const PROGRAM_UTILITY_OPTIONS = orderBy(
    ProgramUtilityList.map(utility => ({ label: utility.name, value: utility.utility_id, key: utility.utility_id })),
    'label'
);

// Static option definitions
const METER_ENROLLMENT_STATUS_OPTIONS = orderBy(
    Object.entries(ENROLLMENT_STATUS_TEXT_MAP).map(([key, value]) => ({
        label: value,
        value: key,
    })),
    'label'
);

// Static option definitions
const NOMINATION_RECENCY_OPTIONS = orderBy(
    Object.entries(NOMINATION_RECENCY_TEXT_MAP).map(([key, value]) => ({
        label: value,
        value: key,
    })),
    'label'
);

const HAS_VEN_OPTIONS = orderBy(
    [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
    ],
    'label'
);

const NOMINATION_PRESET_OPTIONS = orderBy(
    [
        { label: '>0 kW', value: 'more_than_0' },
        { label: 'All', value: 'all' },
    ],
    'label'
);

const SITE_INTERVAL_DATA_OPTIONS = orderBy(
    SITE_INTERVAL_DATA_FILTER_VALUES.map(option => ({
        label: option.text,
        value: option.value,
    })),
    'label'
);

const CLOSED_SITES_OPTIONS: ISelectOption<string>[] = orderBy(
    [
        { label: 'Yes', value: 'true', key: 'yes' },
        { label: 'No', value: 'false', key: 'no' },
    ],
    'label'
);

const DATE_RANGE_PRESET_OPTIONS: ISelectOption<string>[] = orderBy(
    [
        { label: 'All', value: 'all' },
        { label: 'Current Month', value: 'current_month' },
        { label: 'Current Quarter', value: 'current_quarter' },
        { label: 'Previous Quarter', value: 'previous_quarter' },
        { label: 'Year-to-Date', value: 'year_to_date' },
        { label: 'Prior Year', value: 'prior_year' },
    ],
    'label'
);

const VENDOR_BILL_ITEM_PERIOD_OPTIONS: ISelectOption<string>[] = [
    { label: 'All', value: 'all' },
    { label: 'Current Month', value: 'current_month' },
    { label: 'Previous Month', value: 'previous_month' },
    { label: 'Current Quarter', value: 'current_quarter' },
    { label: 'Previous Quarter', value: 'previous_quarter' },
    { label: 'Year-to-Date', value: 'year_to_date' },
    { label: 'Previous Year', value: 'previous_year' },
    { label: 'Custom Range', value: 'custom_range' },
];

export const ALERT_TRACKING_PERIOD_OPTIONS: ISelectOption<string>[] = [
    { label: 'Current Month', value: 'current_month' },
    { label: 'Previous Month', value: 'previous_month' },
    { label: 'Current Quarter', value: 'current_quarter' },
    { label: 'Previous Quarter', value: 'previous_quarter' },
    { label: 'Custom Range', value: 'custom_range' },
];

export const EVENT_COMPANY_PARTICIPATION_PERIOD_OPTIONS: ISelectOption<string>[] = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Custom Range', value: 'custom_range' },
];

const EVENT_DURATION_PRESET_OPTIONS: ISelectOption<string>[] = orderBy(
    [
        { label: '<1 hr', value: 'less_than_1' },
        { label: '1-3 hr', value: '1_to_3' },
        { label: '>3 hr', value: 'more_than_3' },
    ],
    'label'
);

const AGREEMENT_STATUS_OPTIONS: ISelectOption<string>[] = orderBy(
    [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
        { label: 'Expiring in Next 30 Days', value: 'expiring_soon' },
        { label: 'Expired in Last 30 Days', value: 'expired_recently' },
    ],
    'label'
);

const HAS_EVENT_OPTIONS = orderBy(
    [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
    ],
    'label'
);

const INTERVAL_DATA_SOURCE_OPTIONS: ISelectOption<string>[] = orderBy(Object.values(SanInfoSource)).map(source => ({
    label: capitalize(source),
    value: source.toLocaleLowerCase(),
}));

const VEN_TYPE_OPTIONS: ISelectOption<string>[] = orderBy(
    [
        { label: 'Customer VEN', value: 'customer_ven' },
        { label: 'Enersponse GEM', value: 'hardware' },
    ],
    'label'
);

const RESOURCE_TELEMETRY_STATUS_OPTIONS: ISelectOption<string>[] = orderBy(
    [
        { label: 'Online', value: 'online' },
        { label: 'Offline', value: 'offline' },
    ],
    'label'
);

const VEN_STATUS_OPTIONS: ISelectOption<string>[] = orderBy(
    Object.values(VenStatus).map(status => ({
        label: capitalize(status),
        value: status,
    })),
    'label'
);

const MARKET_AWARD_SOURCE_OPTIONS: ISelectOption<string>[] = orderBy(
    [
        { label: 'DAM', value: 'DAM' },
        { label: 'RTD', value: 'RTD' },
        { label: 'FMM', value: 'FMM' },
    ],
    'label'
);

const MARKET_AWARD_STATUS_OPTIONS: ISelectOption<string>[] = orderBy(
    [
        { label: 'NEW', value: 'new' },
        { label: 'APPROVED', value: 'approved' },
        { label: 'DENIED', value: 'denied' },
        { label: 'AUTO', value: 'auto' },
        { label: 'IGNORED', value: 'ignored' },
    ],
    'label'
);

const EVENT_STATUS_OPTIONS: ISelectOption<string>[] = orderBy(
    [
        { label: 'Scheduled', value: 'scheduled' },
        { label: 'Pre-Event', value: 'pre-event' },
        { label: 'Active', value: 'active' },
        { label: 'Completed', value: 'completed' },
        { label: 'Cancelled', value: 'cancelled' },
    ],
    'label'
);

const ALERT_TRACK_STATUS_OPTIONS: ISelectOption<string>[] = orderBy(
    [
        { label: 'Pre-Event', value: 'pre-event' },
        { label: 'Scheduled', value: 'scheduled' },
        { label: 'Active', value: 'active' },
        { label: 'Ongoing', value: 'ongoing' },
        { label: 'Completed', value: 'completed' },
        { label: 'Cancelled', value: 'cancelled' },
        { label: 'Standby', value: 'standby' },
        { label: 'Updated', value: 'updated' },
    ],
    'label'
);

const ALERT_TRACK_OPENED_OPTIONS: ISelectOption<string>[] = [
    { label: 'Opened', value: 'true' },
    { label: 'Not Opened', value: 'false' },
];

const ALERT_TRACK_PARTICIPATION_OPTION: ISelectOption<string>[] = [
    { label: 'Confirmed', value: AlertTrackParticipationStatusType.CONFIRMED },
    { label: 'Partial', value: AlertTrackParticipationStatusType.PARTIAL },
    { label: 'Declined', value: AlertTrackParticipationStatusType.REJECTED },
    { label: 'No Response', value: 'null' },
];

const CLOUD_VEN_DISPATCH_TYPE_OPTIONS: ISelectOption<string>[] = orderBy(
    Object.entries(CloudVenDispatchTypeTableLabelMap).map(([value, label]) => ({
        label,
        value,
    })),
    'label'
);

const NOMINATION_STATUS_OPTIONS: ISelectOption<string>[] = orderBy(
    [
        { label: 'Ready for Customer Review', value: 'ready_for_customer_review' },
        { label: 'Scheduled', value: 'scheduled' },
        { label: 'Skipped', value: 'skipped' },
        { label: 'Sent', value: 'sent' },
        { label: 'Confirmed', value: 'confirmed' },
        { label: 'Rejected', value: 'rejected' },
    ],
    'label'
);

const NOMINATION_READINESS_TRACKING_STATUS_OPTIONS: ISelectOption<string>[] = orderBy(
    [
        { label: 'Scheduled', value: 'scheduled' },
        { label: 'Skipped', value: 'skipped' },
        { label: 'Sent', value: 'sent' },
        { label: 'Confirmed', value: 'confirmed' },
        { label: 'Rejected', value: 'rejected' },
    ],
    'label'
);

const PJM_LOCATION_STATUS_FILTER: ISelectOption<string>[] = [
    { label: 'NEW', value: PjmLocationStatus.NEW },
    { label: 'ACTIVE', value: PjmLocationStatus.ACTIVE },
    { label: 'INACTIVE', value: PjmLocationStatus.INACTIVE },
    { label: 'COMPLETED', value: PjmLocationStatus.COMPLETED },
];

const PJM_ZONE_FILTER: ISelectOption<string>[] = ZONE.map(zone => ({ label: zone, value: zone }));

const PJM_LOCATION_STATE_FILTER: ISelectOption<string>[] = STATE.map(state => ({ label: state, value: state }));

const PJM_EDC_FILTER: ISelectOption<string>[] = EDC_VALUES.map(edc => ({ label: edc, value: edc }));

const PJM_SYNC_DRHUB_STATUS_FILTER: ISelectOption<string>[] = [
    { label: 'PENDING', value: SyncStatus.PENDING },
    { label: 'SUCCESS', value: SyncStatus.SUCCESS },
    { label: 'FAILED', value: SyncStatus.FAILED },
];

const PJM_REGISTRATION_STATUS_FILTER: ISelectOption<string>[] = [
    { label: 'NEW', value: PjmRegistrationStatus.NEW },
    { label: 'CONFIRMED', value: PjmRegistrationStatus.CONFIRMED },
    { label: 'DENIED', value: PjmRegistrationStatus.DENIED },
    { label: 'PENDING_PJM', value: PjmRegistrationStatus.PENDING_PJM },
    { label: 'TERMINATED', value: PjmRegistrationStatus.TERMINATED },
    { label: 'TERMINATED_DUPLICATE', value: PjmRegistrationStatus.TERMINATED_DUPLICATE },
    { label: 'WITHDRAWN', value: PjmRegistrationStatus.WITHDRAWN },
];

const PJM_REGISTRATION_RESOURCE_TYPE_FILTER: ISelectOption<string>[] = [
    { label: 'EMERGENCY', value: PjmResourceType.EMERGENCY },
    { label: 'PRE_EMERGENCY', value: PjmResourceType.PRE_EMERGENCY },
];

const PJM_REGISTRATION_PRODUCT_FILTER: ISelectOption<string>[] = [
    { label: 'BASE_DR', value: PjmProductType.BASE_DR },
    { label: 'ANNUAL_DR', value: PjmProductType.ANNUAL_DR },
    { label: 'CAPACITY_PERFORMANCE_DR', value: PjmProductType.CAPACITY_PERFORMANCE_DR },
    { label: 'EXTENDED_SUMMER_DR', value: PjmProductType.EXTENDED_SUMMER_DR },
    { label: 'LIMITED_DR', value: PjmProductType.LIMITED_DR },
    { label: 'PRICE_RESPONSIVE_DEMAND', value: PjmProductType.PRICE_RESPONSIVE_DEMAND },
];

const PJM_REGISTRATION_LEAD_TIME_FILTER: ISelectOption<string>[] = [
    { label: 'Quick_30', value: PjmRegistrationLeadTime.Quick_30 },
    { label: 'Short_60', value: PjmRegistrationLeadTime.Short_60 },
    { label: 'Long_120', value: PjmRegistrationLeadTime.Long_120 },
];

const PJM_REGISTRATION_YES_NO_FILTER: ISelectOption<string>[] = [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
];

const useCompanyOptions = (companies: ICompany[]) => {
    const allCompaniesOptions: ISelectOption<number | string>[] = useMemo(() => {
        const options: ISelectOption<number | string>[] = companies.map(company => ({
            label: company.company_name,
            value: company.company_id,
            key: company.company_id.toString(),
        }));

        return options;
    }, [companies]);

    const controlProviderOptions: ISelectOption<number | string>[] = useMemo(() => {
        const controlProviderCompanies = filterCompaniesByType(companies, COMPANY_TYPE.CONTROL_PROVIDER);

        const options: ISelectOption<number | string>[] = controlProviderCompanies.map(company => ({
            label: company.company_name,
            value: company.company_id,
            key: company.company_id.toString(),
        }));

        options.unshift({ label: 'No Control Provider', value: 'null' });

        return options;
    }, [companies]);

    const utilityCustomerOptions: ISelectOption<number | string>[] = useMemo(() => {
        const utilityCustomerCompanies = filterCompaniesByType(companies, COMPANY_TYPE.UTILITY_CUSTOMER);

        const options: ISelectOption<number | string>[] = utilityCustomerCompanies.map(company => ({
            label: company.company_name,
            value: company.company_id,
            key: company.company_id.toString(),
        }));

        options.unshift({ label: 'No Utility Customer', value: 'null' });

        return options;
    }, [companies]);

    const customerCompaniesOptions: ISelectOption<number | string>[] = useMemo(() => {
        const customerCompanies = filterCompaniesByType(companies, COMPANY_TYPE.CUSTOMER_COMPANY);

        const options: ISelectOption<number | string>[] = customerCompanies.map(company => ({
            label: company.company_name,
            value: company.company_id,
            key: company.company_id.toString(),
        }));

        return options;
    }, [companies]);

    return { allCompaniesOptions, controlProviderOptions, utilityCustomerOptions, customerCompaniesOptions };
};

const useSiteOptions = (sites: IFilterOption[]): ISelectOption<number | string>[] => {
    return useMemo(() => {
        return orderBy(
            sites
                .filter(item => !!item.text)
                .map(item => ({
                    label: item.text,
                    value: item.value,
                    key: item.value.toString(),
                })) || [],
            'label'
        );
    }, [sites]);
};

const useMeterEnrollmentEnrollmentIdOptions = (enrollmentIds: IFilterOption[]): ISelectOption<string>[] => {
    return useMemo(() => {
        return orderBy(
            enrollmentIds
                .filter(item => !!item.text)
                .map(item => ({
                    label: item.text,
                    value: item.value,
                    key: item.value,
                })) || [],
            'label'
        );
    }, [enrollmentIds]);
};

const useProgramOptions = (programs: IProgram[]): ISelectOption<number | string>[] => {
    return useMemo(() => {
        const programOptions: ISelectOption<number | string>[] = programs.map(program => ({
            label: program.name,
            value: program.program_id,
            key: program.program_id.toString(),
        }));

        programOptions.unshift({ label: 'No Program', value: 'null', key: 'null' });

        return programOptions;
    }, [programs]);
};

const useUtilityOptions = (utilities: string[]): ISelectOption<string>[] => {
    return useMemo(() => {
        return orderBy(
            utilities.map(name => ({
                label: name,
                value: name.toLowerCase(),
                key: name,
            })) || [],
            'label'
        );
    }, [utilities]);
};

const useMarketOptions = (marketZones: { [key: string]: string[] }): ISelectOption<string>[] => {
    return useMemo(() => {
        const marketOptions: ISelectOption<string>[] = orderBy(
            Object.entries(marketZones).map(([key, value]) => ({
                label: key.toUpperCase(),
                value: key,
                key,
            })),
            'label'
        );

        marketOptions.unshift({ label: 'Without Market', value: 'null', key: 'null' });

        return marketOptions;
    }, [marketZones]);
};

const useCaisoResourcesOptions = (resources: ICaisoResource[]): ISelectOption<number | string>[] => {
    return useMemo(() => {
        const resourceOptions: ISelectOption<number | string>[] = resources.map(resource => ({
            label: resource.name,
            value: resource.name,
            key: resource.name,
        }));

        return resourceOptions;
    }, [resources]);
};

const useVendorBillItemProgramOptions = (programs: IFilterOption[]): ISelectOption<string>[] => {
    return useMemo(() => {
        const programOptions: ISelectOption<string>[] = programs.map(program => ({
            label: program.text,
            value: program.value,
            key: program.value,
        }));

        return orderBy(programOptions, 'label');
    }, [programs]);
};

const useVendorBillItemUtilityOptions = (utilities: IFilterOption[]): ISelectOption<string>[] => {
    return useMemo(() => {
        const utilityOptions: ISelectOption<string>[] = utilities.map(utility => ({
            label: utility.text,
            value: utility.value,
            key: utility.value,
        }));

        return orderBy(utilityOptions, 'label');
    }, [utilities]);
};

const useVendorBillTransactionIdOptions = (transactionIds: IFilterOption[]): ISelectOption<string>[] => {
    return useMemo(() => {
        const transactionIdOptions: ISelectOption<string>[] = transactionIds.map(transactionId => ({
            label: transactionId.text,
            value: transactionId.value,
            key: transactionId.value,
        }));

        return orderBy(transactionIdOptions, 'label');
    }, [transactionIds]);
};

const useAlertTrackProgramOptions = (programs: IProgram[]): ISelectOption<string>[] => {
    const programOptions: ISelectOption<string>[] = programs.map(program => ({
        label: program.name,
        value: program.name,
        key: program.name,
    }));

    programOptions.unshift({ label: 'No Program', value: 'null', key: 'null' });

    return programOptions;
};

const useAlertTrackUserOptions = (users: ITrackingUser[]): ISelectOption<string>[] => {
    return users.map(user => ({
        label: user.text,
        value: user.value,
        key: user.value.toString(),
    }));
};

const useEventCompanyParticipationProgramOptions = (programs: IProgram[]): ISelectOption<string>[] => {
    const programOptions: ISelectOption<string>[] = programs.map(program => ({
        label: program.name,
        value: program.name,
        key: program.name,
    }));

    programOptions.unshift({ label: 'No Program', value: 'null', key: 'null' });

    return programOptions;
};

const useUserOptions = (users: IUser[]): ISelectOption<number>[] => {
    return users.map(user => ({
        label: `${user.user_fname} ${user.user_lname}`,
        value: user.user_id,
        key: user.user_id.toString(),
    }));
};

const useAccountMangerOptions = (accountManagers: IFilterOptionValue[]): ISelectOption<string>[] => {
    return useMemo(() => {
        return orderBy(
            accountManagers.map(accountManager => ({
                label: accountManager.value,
                value: accountManager.value.toLowerCase(),
                key: accountManager.value,
            })) || [],
            'label'
        );
    }, [accountManagers]);
};

const usePjmLocationZipCodeOptions = (zipCodes: IFilterOptionValue[]): ISelectOption<string>[] => {
    return useMemo(() => {
        return orderBy(
            zipCodes.map(zipCode => ({
                label: zipCode.value,
                value: zipCode.value,
                key: zipCode.value,
            })) || [],
            'label'
        );
    }, [zipCodes]);
};

const usePjmRegistrationSeasonOptions = (seasons: IFilterOption[]): ISelectOption<string>[] => {
    return useMemo(() => {
        const orderedList = orderBy(
            seasons.map(season => {
                const [seasonStr, yearStr] = season.value.split('-');
                return {
                    text: season.text,
                    value: season.value,
                    season: seasonStr,
                    year: yearStr,
                };
            }) || [],
            ['year', 'season'],
            ['desc', 'desc']
        );

        return orderedList.map(season => ({
            label: season.text,
            value: season.value,
            key: season.value,
        }));
    }, [seasons]);
};
